import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from "moment";

import { post } from "../../services/smartApiService";
import { HOLIDAYS_URLS } from "../../api/ManageUrls";
import { showAlertAutoClose } from "../../services/notifyService";
import { SdsForm } from "soft_digi";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";
import { useSiteContext } from "../../contexts/SiteProvider";


interface HolidaysFormProps {
  loadTableData: () => void;
  data: any;
}


const schema = Yup.object({
  holiday_date: Yup.date().required("Holiday date is required"),
  holiday_name: Yup.string().required("Holiday name is required"),
  exchange: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Exchange is required"),
});


const formatDefaultValues = (data: any) => {
  if (!data) return {};

  return {
    ...data,
    holiday_date: data.holiday_date
      ? new Date(data.holiday_date)
      : null,
    exchange: data.exchange
      ? { label: data.exchange, value: data.exchange }
      : null,
  };
};


const HolidaysForm: React.FC<HolidaysFormProps> = ({
  loadTableData,
  data,
}) => {
  const { closeModal } = useSiteContext();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: formatDefaultValues(data),
  });



  useEffect(() => {
    reset(formatDefaultValues(data));
  }, [data, reset]);



  const onSubmit = (formData: any) => {
    const payload = {
      holiday_name: formData.holiday_name,
      holiday_date: formData.holiday_date
        ? moment(formData.holiday_date).format("YYYY-MM-DD")
        : null,
      exchange: formData.exchange?.value,
    };

    let url = HOLIDAYS_URLS.INSERT;

    if (formData.id) {
      url = `${HOLIDAYS_URLS.UPDATE}${formData.id}`;
    }

    const subscription = post(url, payload).subscribe(
      () => {
        showAlertAutoClose("Holiday saved successfully", "success");
        closeModal();
        loadTableData();
      },
      () => {
        showAlertAutoClose("Failed to save holiday", "error");
      }
    );

    return () => subscription.unsubscribe();
  };


  const formElements: SdsFormElementProps[] = [
    {
      type: "DATE",
      width: 6,
      name: "holiday_date",
      element: {
        label: "Holiday Date",
        placeHolder: "Select holiday date",
        inputType: "BORDER_LABEL",
      },
    },

    {
      type: "TEXT_BOX",
      width: 6,
      name: "holiday_name",
      element: {
        label: "Holiday Name",
        placeHolder: "Enter holiday name",
        inputType: "BORDER_LABEL",
      },
    },

    {
      type: "SELECT_BOX",
      width: 6,
      name: "exchange",
      element: {
        label: "Exchange",
        placeHolder: "Select exchange",
        inputType: "BORDER_LABEL",
        options: [
          { label: "NSE", value: "NSE" },
          { label: "BSE", value: "BSE" },
        ],
      },
    },

    {
      type: "BUTTON",
      width: 12,
      name: "submit",
      element: {
        label: data?.id ? "Update" : "Add",
        classList: ["button", "is-primary"],
        onClick: handleSubmit(onSubmit),
      },
    },
  ];

  return (
    <div className="box">
      <SdsForm elements={formElements} control={control} errors={errors} />
    </div>
  );
};

export default HolidaysForm;
